import { InvoiceModificationComponent } from './../../invoices/invoice-modification/invoice-modification.component';
import { InvoiceUpsertDto, initializedInvoiceUpsertDto } from './../../invoices/dto/invoice-upsert.dto';
import { ReceiptService } from './../shared/receipt/receipt.service';
import { InvoicePaymentHeaderComponent } from './../../invoices/invoice-builder/invoice-payment-header/invoice-payment-header.component';
import { InvoicePaymentDto } from './../shared/invoice-payment/dto/invoice-payment.dto';
import { PaymentSourceService } from './../shared/payment-source/payment-source.service';
import { BatchService } from './../shared/invoice/batch.service';
import { PaymentTypeService } from './../shared/payment-types/payment-type.service';
import { Component, OnInit, OnChanges, SimpleChanges, Input, ViewChild, Output, EventEmitter } from '@angular/core';
import { PaymentTypeDto } from '../shared/payment-types/dto/payment-type.dto';
import { PaymentHeaderDto, initializedPaymentHeaderDto } from 'src/app/invoices/dto/payment-header.dto';
import { PaymentSource } from '../shared/payment-source/payment-source.model';
import { CreditCardPaymentInfo } from '../shared/credit-card/credit-card-payment-info.model';
import { InvoiceService } from '../shared/invoice/invoice.service';
import { RevenueService } from '../shared/revenue.service';
import { CreditCardService } from '../shared/credit-card/credit-card.service';
import { NotificationService, TranslationService, UtilService } from 'src/app/shared';
import { AdvancedCreditCardPaymentResult } from '../shared/credit-card/advanced-credit-card-payment-result.model';
import { AdvancedPaymentInfoPaymentHeaderMapDto } from '../shared/invoice-payment/dto/advanced-payment-info-payment-header-map.dto';
import { MultipleInvoicePaymentDto } from '../shared/invoice-payment/dto/multiple-invoice-payment.dto';
import { MultipleInvoicePaymentResultDto } from '../shared/invoice-payment/dto/multiple-invoice-payment-result.dto';
import { CreditCardRefundRequest } from '../shared/credit-card/credit-card-refund-request.model';
import { InvoiceSearchParams } from '../shared/invoice/invoice-search-params.model';
import { truncateToDecimals } from 'src/app/shared/util/math-utils';
import { distributeAmountPrecisely } from 'src/app/shared/util/payment-utils';
import { ModifiableInvoicesDto } from '../shared/dto/modifiable-invoices.dto';
import { RevenueItemService } from '../shared/revenue-items/revenue-item.service';
import { RevenueItem } from '../shared/revenue-items/revenue-item.model';

@Component({
  selector: 'wl-multi-invoice-payment',
  templateUrl: './multi-invoice-payment.component.html',
  styleUrls: ['./multi-invoice-payment.component.less'],
  providers: [PaymentSourceService, RevenueService, CreditCardService],
    standalone: false
})
export class MultiInvoicePaymentComponent implements OnInit, OnChanges {
  @Input() invoiceNumbers: number[];
  @Input() unappliedPaymentsBalance: number;
  overpaying: boolean;

  @Output() profileIdentified = new EventEmitter<number>();

  @ViewChild(InvoicePaymentHeaderComponent, { static: false }) paymentHeaderComponent: InvoicePaymentHeaderComponent;
  @ViewChild(InvoiceModificationComponent, { static: false }) invoiceModification: InvoiceModificationComponent;

  loading = false;
  isSaving = false;
  paymentHeader: PaymentHeaderDto;
  paymentTypes: PaymentTypeDto[];
  paymentSources: PaymentSource[];
  creditCardPayment: CreditCardPaymentInfo;
  invoicePayments: InvoicePaymentDto[] = [];
  totalAdjustedAmountDue = 0;
  showModificationPrompt = false;
  modificationItemName = '';
  invoiceNumsForModification: Array<number>;
  invoiceToModify = initializedInvoiceUpsertDto();
  invoiceHasBeenModified = false;
  noOpenInvoicesFound = false;
  modifiableInvoices: ModifiableInvoicesDto;
  revenueItems: Array<RevenueItem> = [];
  paymentTypeError: string | undefined = null;

  constructor(
    private _paymentTypeService: PaymentTypeService,
    private _batchService: BatchService,
    private _paymentSourceService: PaymentSourceService,
    private _invoiceService: InvoiceService,
    private _revenueService: RevenueService,
    private _creditCardService: CreditCardService,
    private _translationService: TranslationService,
    private _receiptService: ReceiptService,
    private _utilService: UtilService,
    private _revenueItemService: RevenueItemService,
    private _notificationService: NotificationService
  ) {
    this.paymentHeader = initializedPaymentHeaderDto();
    this.creditCardPayment = new CreditCardPaymentInfo();
  }

  ngOnInit() {
    this._revenueItemService.getAllRevenueItems().then((data) => {
      this.revenueItems = data;
      if (this.invoiceNumbers?.length) {
        this.checkModifiableInvoices();
        this.getInvoices();
      }
    });
    this._paymentTypeService.getPaymentTypes().then((data) => {
      this.paymentTypes = data;
    });

    this._batchService.getNextBatchNumber().then((data) => {
      this.paymentHeader.Batch_Creation = data;
    });

    this._paymentSourceService.getPaymentSources().then((data) => {
      this.paymentSources = data;
    });
  }

  ngOnChanges(simpleChanges: SimpleChanges) {
    for (const propName in simpleChanges) {
      if (propName === 'invoiceNumbers' && this.invoiceNumbers?.length && this.revenueItems?.length) {
        this.checkModifiableInvoices();
        this.getInvoices();
      }
    }
  }

  modificationClosed(invoiceNum: number) {
    const index = this.invoiceNumsForModification.findIndex((x) => x === invoiceNum);
    const nextInvoice = this.invoiceNumsForModification[index + 1];

    this.invoiceNumsForModification.splice(index, 1);

    if (this.invoiceNumsForModification.length > 0) {
      let invoiceToModify = this.modifiableInvoices.ModifiableInvoices.find(
        (x) => x.ModifiableInvoice.InvoiceHeader.InvoiceNum === nextInvoice
      ).ModifiableInvoice;

      const modificationPotential = invoiceToModify.LineItems.filter((x) => x.RelatedRevenueItems.length > 0);
      if (modificationPotential.length > 0) {
        this.modificationItemName = modificationPotential[0].Item;
        this.invoiceToModify = invoiceToModify;
        this.showModificationPrompt = true;
      }
    } else {
      if (this.invoiceHasBeenModified) {
        this.getInvoices();
      }
    }
  }

  invoiceModified() {
    this.invoiceHasBeenModified = true;
  }

  amountChanged() {
    if (this.paymentHeader.Amount > this.unappliedPaymentsBalance) {
      this.paymentHeader.Amount = this.unappliedPaymentsBalance;
    }

    if (this.paymentHeader.Amount > this.totalAdjustedAmountDue) {
      this.paymentHeader.Amount = this.totalAdjustedAmountDue;
    }

    this.overpaying = this.paymentHeader.Amount > this.totalAdjustedAmountDue;

    // 1. First set common fields (if needed)
    this.mapPaymentHeaderToPayments(); // ← this should happen BEFORE distribution

    // 2. Then distribute
    this.distributePaymentToInvoicePayments();
  }

  public newBatch() {
    this._batchService.getNextBatchNumber().then((data) => {
      this.paymentHeader.Batch_Creation = data;
    });
  }

  public resetData(headerFieldsToPreserve: string[] = []) {
    const batch = this.paymentHeader.Batch_Creation;
    const originalPaymentHeader = this.paymentHeader;
    this.paymentHeader = initializedPaymentHeaderDto();
    this.paymentHeader.Batch_Creation = batch;

    if (headerFieldsToPreserve.length > 0) {
      for (let prop of headerFieldsToPreserve) {
        this.paymentHeader[prop] = originalPaymentHeader[prop];
      }
    }
    this.creditCardPayment = new CreditCardPaymentInfo();
  }

  public async applyPayments(): Promise<PaymentsResult> {
    this.isSaving = true;
    if (!this.paymentHeader.Amount || this.paymentHeader.Amount <= 0) {
      this.isSaving = false;
      return Promise.reject({
        messages: [this._translationService.getInstant('revenue.transactions-grid.apply-payments.payment-amount-zero')],
      } as PaymentsResult);
    }
    if (!this.validatePaymentHeader()) {
      this.isSaving = false;
      return Promise.reject({
        messages: [this._translationService.getInstant('revenue.transactions-grid.apply-payments.payment-amount-mismatch')],
      } as PaymentsResult);
    }

    // Split into CC and non-CC payments
    const activePayments = this.invoicePayments.filter((p) => p.Amount > 0);
    const ccPayments = activePayments.filter((p) => p._paymentType?.CreditCardType);
    const nonCcPayments = activePayments.filter((p) => !p._paymentType?.CreditCardType);

    if (ccPayments.length > 0) {
      // Amount must be set before prepareForSubmit so Preczn's guard (!this.value?.Amount) doesn't skip tokenization.
      this.creditCardPayment.Amount = ccPayments.reduce((sum, p) => sum + p.Amount, 0);

      if (this.paymentHeaderComponent) {
        try {
          await this.paymentHeaderComponent.prepareForSubmit();
        } catch (e) {
          this.isSaving = false;
          return Promise.reject({
            messages: [this._utilService.extractErrorMessage(e)],
          } as PaymentsResult);
        }
      }
    }

    // CC payments are grouped by gateway and payment type, then charged via processAdvancedCreditPayments.

    return this.processAdvancedCreditPayments(ccPayments).then(
      (ccResults: Array<AdvancedCreditCardPaymentResult>) => {
        const failedResults = ccResults.filter((r) => !r.TransactionResult.Succeeded);
        const succeededResults = ccResults.filter((r) => r.TransactionResult.Succeeded);

        if (failedResults.length > 0 && succeededResults.length === 0 && nonCcPayments.length === 0) {
          // All CC transactions failed and there are no non-CC payments to fall back on.
          return Promise.reject({
            messages: failedResults.map((r) => r.TransactionResult.RawTransactionResult),
          } as PaymentsResult);
        }

        if (failedResults.length > 0) {
          this._notificationService.translateWarning('revenue.multi-invoice-payment.cc-partial-failure-warning');
        }

        // Build one dto per returned PaymentHeader for succeeded transactions only.
        const ccDtos: MultipleInvoicePaymentDto[] = [];
        for (const r of succeededResults) {
          const gatewayId = r.PaymentInfoPaymentHeaderMap.CreditCardPaymentInfo.CCGatewayID;
          for (const header of r.PaymentInfoPaymentHeaderMap.PaymentHeaders) {
            ccDtos.push({
              PaymentHeader: header,
              InvoicePayments: ccPayments.filter(
                (p) => p._paymentType?.PaymentTypeId === header.PaymentTypeID && p._paymentType?.CcGatewayId === gatewayId
              ),
              PayWithUnappliedPaymentBalance: Boolean(this.unappliedPaymentsBalance),
            });
          }
        }

        // Non-CC payments are grouped by payment type. Each group gets its own PaymentHeader
        const nonCcByPaymentType = new Map<number, InvoicePaymentDto[]>();
        for (const payment of nonCcPayments) {
          const typeId = payment._paymentType.PaymentTypeId;
          if (!nonCcByPaymentType.has(typeId)) nonCcByPaymentType.set(typeId, []);
          nonCcByPaymentType.get(typeId)?.push(payment);
        }

        const nonCcDtos: MultipleInvoicePaymentDto[] = [];
        for (const [, typePayments] of nonCcByPaymentType) {
          const paymentType = typePayments[0]._paymentType;
          nonCcDtos.push({
            PaymentHeader: {
              ...this.paymentHeader,
              Amount: typePayments.reduce((sum, p) => sum + p.Amount, 0),
              _paymentType: paymentType,
              PaymentType: paymentType.PaymentType,
              PaymentTypeID: paymentType.PaymentTypeId,
            },
            InvoicePayments: typePayments,
            PayWithUnappliedPaymentBalance: Boolean(this.unappliedPaymentsBalance),
          });
        }

        // Merge CC and non-CC dtos, then populate each InvoicePaymentDto with fields from its associated PaymentHeader
        const dtos = [...ccDtos, ...nonCcDtos];
        const headerByPaymentType = new Map<number, PaymentHeaderDto>(
          dtos.map((dto) => [dto.PaymentHeader.PaymentTypeID, dto.PaymentHeader])
        );
        this.mapPaymentHeaderToPayments(headerByPaymentType);

        return this._revenueService.applyMultiplePaymentsToInvoices(dtos).then((results: Array<MultipleInvoicePaymentResultDto>) => {
          const anyFailed = results.some((r) => !r.Success);
          if (!anyFailed) {
            const message = this._translationService.getInstant('revenue.transactions-grid.apply-payments.payments-applied');

            if (this.paymentHeaderComponent.sendAutomaticReceipt) {
              const payments = this.invoicePayments.filter((x) => x.Amount > 0);
              const distinctIds = [...new Set(payments.map((x) => x.InvoiceNumber))];
              for (const id of distinctIds) {
                this._receiptService.SendReceipt(id);
              }
            }

            return Promise.resolve({
              messages: [message],
            } as PaymentsResult);
          } else {
            ccResults
              .filter((r) => r.TransactionResult.Succeeded)
              .forEach((r) => {
                const refund = new CreditCardRefundRequest();
                refund.CreditCardTransactionId = +r.TransactionResult.CcId.replace('ccid_', '');
                refund.CreateAdjustments = false;
                refund.RefundAmount = r.PaymentInfoPaymentHeaderMap.CreditCardPaymentInfo.Amount;
                this._creditCardService.refundCreditCardPayment(refund);
              });
            const failedMessages = results.filter((r) => !r.Success && r.Messages?.length).flatMap((r) => r.Messages);
            return Promise.reject({
              messages: failedMessages.length > 0 ? failedMessages : [this._translationService.getInstant('revenue.transactions-grid.apply-payments.credit-problem')],
            } as PaymentsResult);
          }
        });
      },
      (errorResponse) => {
        this.isSaving = false;
        const result = { messages: [] } as PaymentsResult;
        if (errorResponse.Message) {
          result.messages = [errorResponse.Message];
        } else {
          result.messages = [this._translationService.getInstant('revenue.transactions-grid.apply-payments.credit-problem')];
        }
        return Promise.reject(result);
      }
    );
  }

  private checkModifiableInvoices() {
    this._invoiceService.areInvoicesModifiable(this.invoiceNumbers).then((modifiableInvoices: ModifiableInvoicesDto) => {
      this.modifiableInvoices = modifiableInvoices;
      if (modifiableInvoices.HasInvoicesToModify) {
        this.invoiceNumsForModification = [
          ...new Set(modifiableInvoices.ModifiableInvoices.map((x) => x.ModifiableInvoice.InvoiceHeader.InvoiceNum)),
        ];
        const modificationPotential = modifiableInvoices.ModifiableInvoices[0].ModifiableInvoice.LineItems.filter(
          (x) => x.RelatedRevenueItems.length > 0
        );
        if (modificationPotential.length > 0) {
          this.modificationItemName = modificationPotential[0].Item;
          this.invoiceToModify = modifiableInvoices.ModifiableInvoices.find(
            (x) => x.ModifiableInvoice.InvoiceHeader.InvoiceNum === this.invoiceNumsForModification[0]
          )?.ModifiableInvoice;
          this.showModificationPrompt = true;
        }
      }
    });
  }

  private getInvoices() {
    this.loading = true;
    this.noOpenInvoicesFound = false;
    this.paymentTypeError = null;
    this.totalAdjustedAmountDue = 0;
    this._invoiceService
      .searchPayableInvoices({ InvoiceNumbers: this.invoiceNumbers, PageSize: 0, OpenInvoicesOnly: true } as InvoiceSearchParams)
      .then((invoiceResult) => {
        this.loading = false;
        this.invoicePayments = [];
        if (invoiceResult.Result.length > 0) {
          this.paymentHeader.ProfileID = invoiceResult.Result[0].ProfileId;
          this.profileIdentified.emit(this.paymentHeader.ProfileID);
          invoiceResult.Result.forEach((invoice) => {
            invoice.PayableInvoiceLineItems.forEach((lineItem) => {
              lineItem._revenueItem = this.revenueItems.find((x) => x.ItemNum == lineItem.ItemNum) ?? new RevenueItem();
              const payment = {
                InvoiceNumber: invoice.InvoiceNum,
                InvoiceLineItemID: lineItem.LineItemId,
                ProfileID: invoice.ProfileId,
                Amount: 0,
                _lineItem: lineItem,
              } as InvoicePaymentDto;
              this.invoicePayments.push(payment);
              this.totalAdjustedAmountDue += lineItem.AdjustedAmountDue;
              this.totalAdjustedAmountDue = Math.round(this.totalAdjustedAmountDue * 100) / 100;
            });
          });
          this.totalAdjustedAmountDue = this._utilService.round(this.totalAdjustedAmountDue, 2);

          this.onPaymentTypeSelected();
        } else {
          this.noOpenInvoicesFound = true;
        }
      });
  }

  private distributePaymentToInvoicePayments() {
    if (!this.paymentHeader.Amount) {
      return;
    }

    distributeAmountPrecisely(
      this.invoicePayments,
      (payment) => payment._lineItem.AdjustedAmountDue,
      (payment, value) => {
        payment.Amount = value;
      },
      this.paymentHeader.Amount
    );
  }

  private processAdvancedCreditPayments(ccPayments: InvoicePaymentDto[]): Promise<Array<AdvancedCreditCardPaymentResult>> {
    if (!ccPayments.length) {
      return Promise.resolve([]);
    }

    const byGateway = new Map<number, InvoicePaymentDto[]>();
    for (const payment of ccPayments) {
      const gatewayId = payment._paymentType.CcGatewayId;
      if (!byGateway.has(gatewayId)) {
        byGateway.set(gatewayId, []);
      }
      byGateway.get(gatewayId)?.push(payment);
    }

    const maps: AdvancedPaymentInfoPaymentHeaderMapDto[] = [];

    for (const [gatewayId, gatewayPayments] of byGateway) {
      const groupAmount = gatewayPayments.reduce((sum, p) => sum + p.Amount, 0);

      const ccInfo: CreditCardPaymentInfo = { ...this.creditCardPayment };
      ccInfo.CCGatewayID = gatewayId;
      ccInfo.Amount = groupAmount;
      ccInfo.ProfileId = this.paymentHeader.ProfileID;

      const byPaymentType = new Map<number, InvoicePaymentDto[]>();
      for (const payment of gatewayPayments) {
        const typeId = payment._paymentType.PaymentTypeId;
        if (!byPaymentType.has(typeId)) byPaymentType.set(typeId, []);
        byPaymentType.get(typeId)?.push(payment);
      }

      const paymentHeaders: PaymentHeaderDto[] = [];
      for (const [, typePayments] of byPaymentType) {
        const paymentType = typePayments[0]._paymentType;
        const header: PaymentHeaderDto = {
          ...this.paymentHeader,
          Amount: typePayments.reduce((sum, p) => sum + p.Amount, 0),
          _paymentType: paymentType,
          PaymentType: paymentType.PaymentType,
          PaymentTypeID: paymentType.PaymentTypeId,
        };
        this._creditCardService.fillPaymentHeaderFieldsWithCcInfo(ccInfo, header);
        paymentHeaders.push(header);
      }

      maps.push({ PaymentHeaders: paymentHeaders, CreditCardPaymentInfo: ccInfo });
    }

    return this._creditCardService.processAdvancedCreditCardPayments(maps);
  }

  private mapPaymentHeaderToPayments(headerByPaymentType?: Map<number, PaymentHeaderDto>) {
    this.invoicePayments.forEach((payment) => {
      const header = headerByPaymentType?.get(payment._paymentType?.PaymentTypeId) ?? this.paymentHeader;
      for (const property in header) {
        if (property !== 'Amount' && property !== '_paymentType') {
          payment[property] = header[property];
        }
      }
    });
  }

  get hasPaymentMissingType(): boolean {
    return this.invoicePayments.some((p) => p.Amount > 0 && !p._paymentType);
  }

  get anyInvoicePaymentIsCreditCard(): boolean {
    return this.invoicePayments.some((p) => p._paymentType?.CreditCardType);
  }

  onPaymentTypeSelected() {
    this.paymentTypeError = null;
    const selectedPaymentType = this.paymentHeader._paymentType;
    if (!selectedPaymentType) return;
    const hasMatchingItems = this.invoicePayments.some(
      (p) => p._lineItem?._revenueItem?.AccountingPackageId === selectedPaymentType.AccountingPackageId
    );
    if (!hasMatchingItems) {
      this.paymentTypeError = this._translationService.getInstant(
        'revenue.multi-invoice-payment.invalid-payment-type-for-line-items'
      );
      return;
    }
    this.invoicePayments.forEach((payment) => {
      if (payment._lineItem?._revenueItem?.AccountingPackageId === selectedPaymentType.AccountingPackageId) {
        payment._paymentType = selectedPaymentType;
      }
    });
  }

  isPaymentTypeLockedByHeader(payment: InvoicePaymentDto): boolean {
    const headerPaymentType = this.paymentHeader._paymentType;
    if (!headerPaymentType) return false;
    return headerPaymentType.AccountingPackageId === payment._lineItem?._revenueItem?.AccountingPackageId;
  }

  getPaymentTypesForPayment(payment: InvoicePaymentDto): PaymentTypeDto[] {
    if (!this.paymentTypes) return [];
    const accountingPackageId = payment._lineItem?._revenueItem?.AccountingPackageId ?? 0;
    return this.paymentTypes.filter((pt) => pt.AccountingPackageId === accountingPackageId);
  }

  private validatePaymentHeader(): boolean {
    let paymentHeaderAmount = this.paymentHeader.Amount;
    let totalPaymentAmount = this._utilService.round(
      this.invoicePayments.reduce((sum, payment) => sum + payment.Amount, 0),
      2
    );
    return paymentHeaderAmount > 0 && paymentHeaderAmount == totalPaymentAmount;
  }
}

interface PaymentsResult {
  messages: string[];
}
